/**
 * UES (Unified Event System) tracking utilities for The Inside
 *
 * Runs alongside existing Segment/GTM tracking.
 * The UES client automatically:
 * - Captures click IDs from URL (fbclid, gclid, ttclid, etc.)
 * - Persists anonymous ID via first-party cookies
 * - Pushes to window.dataLayer with ues_ prefix (for GTM Pixel Bridge)
 * - Sends events server-side to the UES Lambda endpoint
 */

import { createEventClient } from '@ownedoperated/unified-event-system/client';
import { v5 as uuidv5 } from 'uuid';

const UES_NAMESPACE = 'f47ac10b-58cc-4372-a567-0e02b2c3d479';

function deterministicPurchaseEventId(orderUuid) {
  return uuidv5('purchase:' + orderUuid, UES_NAMESPACE);
}

// Singleton client instance
let client = null;

/**
 * Get or create the UES EventClient singleton
 */
function getClient() {
  if (client) return client;

  const endpoint = window.uesEndpoint;
  if (!endpoint) {
    console.warn('[UES] No endpoint configured (window.uesEndpoint is empty)');
    return null;
  }

  const headers = {};
  if (window.uesApiKey) {
    headers['X-API-Key'] = window.uesApiKey;
  }

  client = createEventClient({
    endpoint,
    headers,
    debug: window.environment !== 'production',
    autoCapture: true,
    // Bind ga_session_id capture to OUR GA4 stream's _ga_<MID> cookie
    // (SDK >= 0.9.4; injected by app.blade.php from UES_GA4_MEASUREMENT_ID).
    // Without this the SDK takes the first _ga_* cookie it finds — the
    // 2026-07-22 incident where a second cookie (this property's
    // MonsterInsights /blog stream) made 100% of purchases land Unassigned.
    ga4MeasurementId: window.uesGa4MeasurementId
  });

  // Identity rehydrate from the `_oo_user` cookie is handled inside the SDK
  // itself (`restoreUserData` at createEventClient time). We previously had
  // a manual rehydrate block here that read long-key `email`/`external_id`
  // fields — but the SDK's actual cookie format uses short keys (`e`/`id`),
  // so the wrapper never matched and was dead code. The Laravel backend
  // (App\Services\UesIdentityCookie) writes the cookie in the SDK's
  // short-key format on login/signup, and the SDK reads it on init.

  return client;
}

// ─── User data for enhanced matching ─────────────────────────────────

/**
 * Set user data on the UES client for enhanced matching
 * Call when user logs in or user data becomes available
 * @param {Object} user - The Inside user object from Redux state
 */
export function uesSetUser(user) {
  const tracker = getClient();
  if (!tracker || !user) return;

  const externalId =
    user.id != null && user.id !== ''
      ? String(user.id)
      : undefined;

  tracker.setUser({
    email: user.email || undefined,
    external_id: externalId,
    first_name: user.first_name || undefined,
    last_name: user.last_name || undefined,
    phone: user.phone || undefined
  });
}

/**
 * Clear user data from the UES client (call on logout)
 */
export function uesClearUser() {
  const tracker = getClient();
  if (!tracker) return;

  tracker.clearUser();
}

/**
 * Enrich UES identity with shipping-address fields (phone, city, state, zip,
 * country). The SDK's setUser merges shallowly with existing identity (email,
 * first_name, last_name, external_id from uesSetUser on login), so this
 * complements without clobbering.
 *
 * Country is hardcoded to 'us' because The Inside ships US-only and TI's
 * GraphQL `State` type does not expose a country field. Server-side
 * SendUesPurchaseEvent resolves the real country via the Eloquent
 * State -> Country relation; Meta merges user_data across same-event_id
 * CAPI events so the canonical value still lands.
 *
 * Call before firing any event where Meta CAPI / Google Ads can benefit from
 * city/state/zip/phone match keys: purchase (uesTrackPurchase) and
 * add_payment_info (uesTrackAddPaymentInfo call sites).
 *
 * `state.code` is sourced uniformly across paths: authenticated
 * createShippingAddress mutation returns `{ id, code, name }`, and
 * StateAutoComplete's handleChange now writes code into the guest form's
 * local state too. Callers don't need a lookup table here.
 *
 * @param {Object} shipping - { city?, zip?, phone?, state?: { code? } }
 */
export function uesSetShippingIdentity(shipping) {
  const tracker = getClient();
  if (!tracker || !shipping) return;
  const stateCode =
    shipping.state && shipping.state.code ? shipping.state.code : undefined;
  tracker.setUser({
    phone: shipping.phone || undefined,
    city: shipping.city || undefined,
    state: stateCode,
    zip: shipping.zip || undefined,
    country: 'us'
  });
}

// ─── Helper: map The Inside product to UES EcommerceItem ──────────────

function mapProductToItem(product, quantity) {
  if (!product) return null;

  return {
    product_id: String(product.id),
    product_name: product.name || undefined,
    sku: product.sku || undefined,
    price: product.price ? parseFloat(product.price) : undefined,
    quantity: quantity != null ? parseInt(quantity, 10) : 1,
    category:
      product.frame && product.frame.subcategories && product.frame.subcategories.length > 0
        ? product.frame.subcategories[0].category.name
        : undefined,
    is_swatch: Boolean(product.is_swatch)
  };
}

function mapCartProductsToItems(cartProducts) {
  if (!cartProducts || !Array.isArray(cartProducts)) return [];

  return cartProducts
    .map(cp => {
      const product = cp.product;
      if (!product) return null;
      return mapProductToItem(product, cp.quantity);
    })
    .filter(Boolean);
}

function computeCartValue(cartProducts) {
  if (!cartProducts || !Array.isArray(cartProducts)) return 0;

  return cartProducts.reduce((total, cp) => {
    const product = cp.product;
    if (!product) return total;
    return total + parseFloat(product.price || 0) * parseFloat(cp.quantity || 1);
  }, 0);
}

// ─── Exported tracking functions ──────────────────────────────────────

/**
 * Track a page view
 */
export function uesPage() {
  const tracker = getClient();
  if (!tracker) return;

  tracker.page();
}

/**
 * Track product viewed
 * @param {Object} product - The Inside product object
 */
export function uesTrackProductViewed(product) {
  const tracker = getClient();
  if (!tracker || !product) return;

  tracker.viewProduct({
    product_id: String(product.id),
    product_name: product.name,
    sku: product.sku,
    price: product.price ? parseFloat(product.price) : undefined,
    category:
      product.frame && product.frame.subcategories && product.frame.subcategories.length > 0
        ? product.frame.subcategories[0].category.name
        : undefined
  });
}

/**
 * Track product clicked
 * @param {Object} product - The Inside product object
 */
export function uesTrackProductClicked(product) {
  const tracker = getClient();
  if (!tracker || !product) return;

  tracker.track('product_clicked', {
    ecommerce: {
      currency: 'USD',
      items: [mapProductToItem(product, 1)]
    }
  });
}

/**
 * Track add to cart
 * @param {Object} product - The Inside product object
 * @param {number} quantity - Quantity added
 */
export function uesTrackAddToCart(product, quantity) {
  const tracker = getClient();
  if (!tracker || !product) return;

  tracker.addToCart({
    product_id: String(product.id),
    product_name: product.name,
    sku: product.sku,
    price: product.price ? parseFloat(product.price) : undefined,
    quantity: quantity != null ? parseInt(quantity, 10) : 1,
    category:
      product.frame && product.frame.subcategories && product.frame.subcategories.length > 0
        ? product.frame.subcategories[0].category.name
        : undefined,
    is_swatch: Boolean(product.is_swatch)
  });
}

/**
 * Track remove from cart
 * @param {Object} product - The Inside product object
 * @param {number} quantity - Quantity removed
 */
export function uesTrackRemoveFromCart(product, quantity) {
  const tracker = getClient();
  if (!tracker || !product) return;

  tracker.removeFromCart({
    product_id: String(product.id),
    product_name: product.name,
    sku: product.sku,
    price: product.price ? parseFloat(product.price) : undefined,
    quantity: quantity != null ? parseInt(quantity, 10) : 1
  });
}

/**
 * Track cart viewed
 * @param {Array} cartProducts - Array of cart product objects ({ product, quantity })
 */
export function uesTrackCartViewed(cartProducts) {
  const tracker = getClient();
  if (!tracker) return;

  const items = mapCartProductsToItems(cartProducts);
  const value = computeCartValue(cartProducts);

  tracker.track('cart_viewed', {
    ecommerce: {
      currency: 'USD',
      value,
      items
    }
  });
}

/**
 * Track checkout started
 * @param {Array} cartProducts - Array of cart product objects ({ product, quantity })
 */
export function uesTrackCheckoutStarted(cartProducts) {
  const tracker = getClient();
  if (!tracker) return;

  const items = mapCartProductsToItems(cartProducts);
  const value = computeCartValue(cartProducts);

  tracker.beginCheckout({
    currency: 'USD',
    value,
    items
  });
}

/**
 * Track purchase / order completed
 * @param {Object} order - The Inside order object
 */
export function uesTrackPurchase(order) {
  const tracker = getClient();
  if (!tracker || !order) return;

  const items = (order.orderProducts || []).map(op => {
    if (op.product) {
      return mapProductToItem(op.product, op.quantity);
    }
    // Gift card
    if (op.giftCard) {
      return {
        product_id: 'gift-card',
        product_name: 'Gift Card',
        sku: 'gift-card',
        price: parseFloat(op.giftCard.original_balance || 0),
        quantity: parseInt(op.quantity, 10) || 1,
        is_swatch: false
      };
    }
    return null;
  }).filter(Boolean);

  // Determine order type based on product mix
  const hasSwatches = items.some(item => item.is_swatch);
  const hasProducts = items.some(item => !item.is_swatch);
  let orderType;
  if (hasSwatches && hasProducts) {
    orderType = 'mixed';
  } else if (hasSwatches) {
    orderType = 'swatch_only';
  } else {
    orderType = 'product_only';
  }

  const totalOrders = order.user && order.user.totalSuccessfulOrders
    ? Number(order.user.totalSuccessfulOrders)
    : undefined;

  uesSetShippingIdentity(order.shippingAddress);

  tracker.track('purchase', {
    event_id: order.uuid ? deterministicPurchaseEventId(order.uuid) : undefined,
    ecommerce: {
      currency: 'USD',
      value: parseFloat(order.total || 0),
      order_id: String(order.id),
      order_type: orderType,
      subtotal: parseFloat(order.subtotal || 0),
      tax: parseFloat(order.taxes || 0),
      shipping: parseFloat(order.shipping_cost || 0),
      discount: parseFloat(order.discounts || 0),
      coupon_code:
        order.discountsRelation && order.discountsRelation.length > 0
          ? order.discountsRelation[0].code
          : undefined,
      is_new_customer: totalOrders !== undefined ? totalOrders <= 1 : undefined,
      customer_order_count: totalOrders,
      items
    }
  });
}

/**
 * Send the HDYHAU `survey_completed` event to UES (warehouse sink only).
 * Routing, warehouse landing, and dedupe are handled server-side; the frontend
 * just POSTs one well-formed event. `email` supplies user_data (the Rockerbox
 * join key); `order.id` is the primary warehouse join key.
 *
 * @param {Object} args
 * @param {Object} args.order      - order ({ id, user: { email } })
 * @param {Object} args.properties - UES survey payload (see buildSurveyPayload)
 */
export function uesTrackSurveyCompleted({ order, properties }) {
  const tracker = getClient();
  if (!tracker || !order) return;

  if (order.user && order.user.email) {
    tracker.setUser({ email: order.user.email });
  }

  const ts = Math.floor(Date.now() / 1000);
  tracker.track('survey_completed', {
    event_id: `survey_completed-${order.id}-${ts}`,
    ecommerce: { order_id: String(order.id) },
    properties
  });
}

/**
 * Track search
 * @param {string} query - Search query string
 */
export function uesTrackSearch(query) {
  const tracker = getClient();
  if (!tracker) return;

  tracker.search(query);
}

/**
 * Track collection / product list viewed
 * @param {string} collectionName - Collection or category name
 * @param {Array} products - Array of product objects
 */
export function uesTrackCollectionViewed(collectionName, products) {
  const tracker = getClient();
  if (!tracker) return;

  const items = (products || []).map(p => mapProductToItem(p, 1)).filter(Boolean);

  tracker.track('collection_viewed', {
    ecommerce: {
      currency: 'USD',
      items
    },
    properties: {
      collection_name: collectionName
    }
  });
}

/**
 * Track a generic custom event
 * @param {string} eventName - Event name
 * @param {Object} properties - Custom properties
 */
export function uesTrackCustomEvent(eventName, properties) {
  const tracker = getClient();
  if (!tracker) return;

  tracker.track(eventName, { properties });
}

/**
 * Track Add Payment Info event. Fire when the user's payment method is
 * *confirmed* — the underlying payment service's tokenization callback returns
 * successfully (Stripe / PayPal / Affirm / Amazon Pay / Apple Pay / Google
 * Pay) — not when a method is selected and not at final submit.
 *
 * @param {Object} args
 * @param {number} args.value - Cart value at this point in checkout
 * @param {string} [args.currency='USD']
 * @param {Array}  [args.items] - Cart items (mapProductToItem output)
 * @param {string} [args.payment_method] - Code: 'credit_card' | 'paypal' | 'amazon_pay' | 'affirm' | 'apple_pay' | 'google_pay'
 */
export function uesTrackAddPaymentInfo({ value, currency = 'USD', items, payment_method } = {}) {
  const tracker = getClient();
  if (!tracker) return;

  tracker.track('add_payment_info', {
    ecommerce: {
      currency,
      value: value != null ? parseFloat(value) : undefined,
      items: items || []
    },
    properties: payment_method ? { payment_method } : undefined
  });
}

/**
 * Get the current UES identity blob as a JSON string, ready to pass to the
 * Checkout mutation as `ues_identity`. Returns null when no identity exists
 * or the SDK isn't initialized.
 */
export function uesGetIdentity() {
  const tracker = getClient();
  if (!tracker) return null;
  try {
    const identity = tracker.getIdentity();
    if (!identity || Object.keys(identity).length === 0) return null;
    return JSON.stringify(identity);
  } catch (e) {
    console.error('[UES] getIdentity error:', e);
    return null;
  }
}
